package com.exura.features.publicplans.data.repository

import com.exura.features.plandefinition.domain.model.PlanContentSummary
import com.exura.features.publicplans.domain.PublicPlansConstants
import com.exura.features.publicplans.domain.model.AnonymousPublicPlanOrder
import com.exura.features.publicplans.domain.model.ProviderPublicPlan
import com.exura.features.publicplans.domain.model.PublicPlanServingEntry
import com.exura.features.publicplans.domain.model.PublicPlanStatistics
import com.exura.features.publicplans.domain.repository.ProviderPublicPlanRepository
import com.exura.features.storage.domain.repository.EntityRepository
import com.exura.features.storage.domain.repository.nested
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.first
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime


class ProviderPublicPlanRepositoryImpl(
    private val publicPlanStorage: EntityRepository<ProviderPublicPlan>,
    private val publicPlanServingStorage: EntityRepository<PublicPlanServingEntry>,
    private val publicPlanOrderStorage: EntityRepository<AnonymousPublicPlanOrder>,
) : ProviderPublicPlanRepository {
    override suspend fun read(id: String): Result<ProviderPublicPlan> = publicPlanStorage.read(id)

    override suspend fun list(): Flow<List<ProviderPublicPlan>> = publicPlanStorage.list()

    override suspend fun createPublicPlan(
        globalEntryId: String,
        planId: String,
        summary: PlanContentSummary,
        websiteLink: String,
        qrCodeUri: String,
        accessCode: String,
    ): Result<ProviderPublicPlan> = runCatching {
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())

        val publicPlan = ProviderPublicPlan(
            id = null,
            globalEntryId = globalEntryId,
            isSharingEnabled = true,
            websiteLink = websiteLink,
            qrCodeUri = qrCodeUri,
            accessCode = accessCode,
            createdDate = now,
            lastModifiedDate = now,
            summary = summary,
            statistics = PublicPlanStatistics(),
            history = emptyList(),
        )
        val createdPlan = publicPlanStorage.create(publicPlan).getOrThrow()

        val publicPlanId = createdPlan.id
        val servingPrefix = "${PublicPlansConstants.Firestore.PROVIDER_PUBLIC_PLANS_COLLECTION_NAME}/${publicPlanId}"

        publicPlanServingStorage.nested(servingPrefix) {
            val servingEntry = PublicPlanServingEntry(
                id = null,
                sharedFrom = now,
                sharedTo = null,
                sharedPlanId = planId,
                summary = summary,
            )
            publicPlanServingStorage.create(servingEntry).getOrThrow()
        }
        createdPlan
    }

    override suspend fun disableSharing(id: String) : Result<Unit> = runCatching {
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        val currentPlan = publicPlanStorage.read(id).getOrThrow()
        val updatedPlan = currentPlan.copy(
            isSharingEnabled = false,
            lastModifiedDate = now,
        )
        publicPlanStorage.set(id, updatedPlan).getOrThrow()
    }


    override suspend fun enableSharing(id: String) : Result<Unit> = runCatching {
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        val currentPlan = publicPlanStorage.read(id).getOrThrow()
        val updatedPlan = currentPlan.copy(
            isSharingEnabled = true,
            lastModifiedDate = now,
        )
        publicPlanStorage.set(id, updatedPlan).getOrThrow()
    }


    override suspend fun replacePlanDefinition(id: String, newPlanId: String, newSummary: PlanContentSummary): Result<Unit> = runCatching {
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        val servingPrefix = "${PublicPlansConstants.Firestore.PROVIDER_PUBLIC_PLANS_COLLECTION_NAME}/${id}"

        publicPlanServingStorage.nested(servingPrefix) {
            // Collect the current serving entries
            val entries = publicPlanServingStorage.list().first()

            val currentEntry = entries.find { it.sharedTo == null }

            currentEntry?.let { entry ->
                val updatedEntry = entry.copy(sharedTo = now)
                publicPlanServingStorage.set(updatedEntry.id!!, updatedEntry).getOrThrow()
            }

            // Create a new serving entry for the replacement
            val newEntry = PublicPlanServingEntry(
                id = null,
                sharedFrom = now,
                sharedTo = null,
                sharedPlanId = newPlanId,
                summary = newSummary,
            )
            publicPlanServingStorage.create(newEntry).getOrThrow()
        }

        val currentPlan = publicPlanStorage.read(id).getOrThrow()
        val updatedPlan = currentPlan.copy(
            summary = newSummary,
            lastModifiedDate = now
        )
        publicPlanStorage.set(id, updatedPlan).getOrThrow()


    }

    override suspend fun getPublicPlanServingHistory(id: String): Flow<List<PublicPlanServingEntry>> {
        val servingPrefix = "${PublicPlansConstants.Firestore.PROVIDER_PUBLIC_PLANS_COLLECTION_NAME}/${id}"
        return publicPlanServingStorage.nested(servingPrefix) {
            publicPlanServingStorage.list()
        }
    }

    override suspend fun getPublicPlanOrders(id: String): Flow<List<AnonymousPublicPlanOrder>> {
        val orderPrefix = "${PublicPlansConstants.Firestore.PROVIDER_PUBLIC_PLANS_COLLECTION_NAME}/${id}"
        return publicPlanOrderStorage.nested(orderPrefix) {
            publicPlanOrderStorage.list()
        }
    }
}
